"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion, useReducedMotion } from "motion/react";
import type { Variants } from "motion/react";
import { ChevronDown, ShieldCheck, MapPin } from "lucide-react";

// ── Link data ────────────────────────────────────────────────────────────────

const COLUMNS: { title: string; links: { label: string; href: string }[] }[] = [
  {
    title: "Product",
    links: [
      { label: "Buy Gold", href: "/download" },
      { label: "Sell Gold", href: "/download" },
      { label: "Live Prices", href: "/gold-silver-prices" },
      { label: "Gold Rate by Purity", href: "/prices" },
      { label: "Gold Calculator", href: "/#calculator-heading" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About Us", href: "/about-us" },
      { label: "For Business", href: "/partner-with-us" },
      { label: "Blog", href: "/blogs" },
      { label: "FAQ's", href: "/faqs" },
      { label: "Contact Us", href: "/contact-us" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Terms & Conditions", href: "/terms-and-conditions" },
      { label: "Terms of Use", href: "/terms-of-use" },
      { label: "Privacy Policy", href: "/privacy-policy" },
      { label: "Disclaimer", href: "/disclaimer" },
      { label: "Refund Policy", href: "/refund-policy" },
      { label: "Conflict of Interest", href: "/conflict-of-interest" },
      { label: "Shariah Certificate", href: "/shariah" },
      { label: "Licenses", href: "/licenses" },
    ],
  },
];

// ── Social icons (inline: brand glyphs are not in this lucide build) ──────────

const SOCIALS: { label: string; href: string; path: string }[] = [
  {
    label: "Instagram",
    href: "https://www.instagram.com/justgold.app/",
    path: "M12 2.2c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.7 3.7 0 0 1-1.38-.9 3.7 3.7 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23C2.21 15.58 2.2 15.2 2.2 12s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.21 8.8 2.2 12 2.2Zm0 1.8c-3.15 0-3.5.01-4.74.07-.9.04-1.38.19-1.71.32-.43.17-.74.37-1.06.69-.32.32-.52.63-.69 1.06-.13.33-.28.81-.32 1.71C3.42 8.5 3.4 8.85 3.4 12s.02 3.5.08 4.74c.04.9.19 1.38.32 1.71.17.43.37.74.69 1.06.32.32.63.52 1.06.69.33.13.81.28 1.71.32 1.24.06 1.59.07 4.74.07s3.5-.01 4.74-.07c.9-.04 1.38-.19 1.71-.32.43-.17.74-.37 1.06-.69.32-.32.52-.63.69-1.06.13-.33.28-.81.32-1.71.06-1.24.07-1.59.07-4.74s-.01-3.5-.07-4.74c-.04-.9-.19-1.38-.32-1.71a2.86 2.86 0 0 0-.69-1.06 2.86 2.86 0 0 0-1.06-.69c-.33-.13-.81-.28-1.71-.32C15.5 4.01 15.15 4 12 4Zm0 3.06A4.94 4.94 0 1 1 12 16.94 4.94 4.94 0 0 1 12 7.06Zm0 1.8a3.14 3.14 0 1 0 0 6.28 3.14 3.14 0 0 0 0-6.28Zm5.14-.94a1.15 1.15 0 1 1-2.3 0 1.15 1.15 0 0 1 2.3 0Z",
  },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/justgold/",
    path: "M6.94 5.5a1.94 1.94 0 1 1-3.88 0 1.94 1.94 0 0 1 3.88 0ZM3.3 8.4h3.28V21H3.3V8.4Zm5.34 0h3.14v1.72h.05c.44-.83 1.5-1.7 3.1-1.7 3.31 0 3.92 2.18 3.92 5.02V21h-3.27v-5.57c0-1.33-.02-3.03-1.85-3.03-1.85 0-2.13 1.45-2.13 2.94V21H8.64V8.4Z",
  },
  {
    label: "Facebook",
    href: "https://www.facebook.com/justgoldapp",
    path: "M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.5 1.49-3.89 3.78-3.89 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.78l-.44 2.89h-2.34v6.99A10 10 0 0 0 22 12Z",
  },
];

// ── Motion ──────────────────────────────────────────────────────────────────

const container: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06, delayChildren: 0.02 } },
};

function fadeUp(reduce: boolean): Variants {
  return {
    hidden: { opacity: 0, y: reduce ? 0 : 16 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.2, 0, 0, 1] },
    },
  };
}

// ── Link column (collapses long columns behind "View more") ──────────────────

const COLUMN_PREVIEW_COUNT = 5;

function FooterLinkColumn({
  col,
  reduce,
}: {
  col: (typeof COLUMNS)[number];
  reduce: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const isLong = col.links.length > COLUMN_PREVIEW_COUNT;
  const visibleLinks =
    isLong && !expanded ? col.links.slice(0, COLUMN_PREVIEW_COUNT) : col.links;

  return (
    <motion.nav variants={fadeUp(reduce)} aria-label={col.title}>
      <h3 className="text-sm font-bold text-text-heading">{col.title}</h3>
      <ul className="mt-5 flex flex-col gap-3">
        {visibleLinks.map((link) => (
          <li key={link.label}>
            <Link
              href={link.href}
              className="text-sm text-text-muted transition-colors hover:text-primary-900"
            >
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
          className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary-600 transition-colors hover:text-primary-900"
        >
          {expanded ? "View less" : "View more"}
          <ChevronDown
            className={`h-3.5 w-3.5 transition-transform ${expanded ? "rotate-180" : ""}`}
            aria-hidden="true"
          />
        </button>
      )}
    </motion.nav>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────

export function Footer() {
  const reduce = useReducedMotion() ?? false;
  const year = new Date().getFullYear();

  return (
    <footer className="relative overflow-hidden border-t border-[#e3ecfb] bg-white text-primary-700">
      {/* Ambient gold glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-[-160px] h-[320px] w-[720px] -translate-x-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(216,154,44,0.08)_0%,transparent_70%)]"
      />

      <motion.div
        variants={container}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.15 }}
        className="relative mx-auto max-w-[1440px] px-6 pt-16 sm:px-10 lg:px-20 lg:pt-20"
      >
        <div className="grid gap-12 lg:grid-cols-[1.4fr_1fr_1fr_1fr]">
          {/* Brand column */}
          <motion.div variants={fadeUp(reduce)} className="max-w-[340px]">
            <Link href="/" aria-label="JustGold Home">
              <Image
                src="/just-gold-logo.png"
                alt="JustGold logo"
                width={274}
                height={100}
                className="h-10 w-auto"
              />
            </Link>
            <p className="mt-5 text-sm leading-relaxed text-text-body">
              Buy, sell and redeem real 24K gold and 999 silver from your
              phone. Held in insured Dubai vaults by Loomis International.
              Shariah certified.
            </p>
          </motion.div>

          {/* Link columns */}
          {COLUMNS.map((col) => (
            <FooterLinkColumn key={col.title} col={col} reduce={reduce} />
          ))}
        </div>

        {/* Trust strip */}
        <motion.div
          variants={fadeUp(reduce)}
          className="mt-14 flex flex-col gap-3 border-t border-[#e3ecfb] py-6 sm:flex-row sm:items-center sm:justify-between"
        >
          <p className="inline-flex items-center gap-2 text-sm text-text-muted">
            <ShieldCheck className="h-4 w-4 text-accent-500" strokeWidth={2} />
            Backed by real 24K gold, vaulted and insured in Dubai. Shariah
            compliant.
          </p>
          <p className="inline-flex items-center gap-2 text-sm text-text-muted">
            <MapPin className="h-4 w-4 text-accent-500" strokeWidth={2} />
            Dubai, United Arab Emirates
          </p>
        </motion.div>

        {/* Bottom bar */}
        <motion.div
          id="footer-bottom-bar"
          variants={fadeUp(reduce)}
          className="flex flex-col-reverse items-center gap-6 border-t border-[#e3ecfb] py-7 sm:flex-row sm:justify-between"
        >
          <p className="text-xs text-text-muted">
            © {year} JustGold. All rights reserved.
          </p>
          <div className="flex items-center gap-3">
            <div
              {...{ "google-add-preferred-source-btn": "" }}
              data-theme="light"
            />
            <div className="flex items-center gap-2.5">
            {SOCIALS.map((s) => (
              <a
                key={s.label}
                href={s.href}
                aria-label={s.label}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-[#e3ecfb] text-primary-500 transition-colors hover:border-accent-400/60 hover:bg-accent-50 hover:text-primary-900"
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
                  <path d={s.path} />
                </svg>
              </a>
            ))}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </footer>
  );
}
